import { Component, inject, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { IftaLabel } from 'primeng/iftalabel';
import { Select } from 'primeng/select';
import { RequestRevisionModalData } from './request-revision-modal-data';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Textarea } from 'primeng/textarea';
import { Button } from 'primeng/button';
import { firstValueFrom, map, shareReplay, startWith } from 'rxjs';
import { NavigationService } from 'app/core/services/navigation.service';
import { RevisionReason } from 'app/core/models';

@Component({
  selector: 'wl-request-revision-modal',
  standalone: true,
  imports: [CommonModule, IftaLabel, Select, ReactiveFormsModule, TranslatePipe, Textarea, Button],
  templateUrl: './request-revision-modal.component.html',
  styleUrl: './request-revision-modal.component.scss',
})
export class RequestRevisionModalComponent implements OnInit {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _fb = inject(FormBuilder);
  private readonly _config = inject<DynamicDialogConfig<RequestRevisionModalData>>(DynamicDialogConfig);
  private readonly _navigationService = inject(NavigationService);

  protected readonly form = this._fb.group(
    {
      reasonId: this._fb.control<null | number>(
        { disabled: false, value: null },
        { validators: [Validators.required] }
      ),
      comment: this._fb.control<null | string>({ disabled: false, value: null }),
    },
    {
      asyncValidators: async ctrl => {
        const reason = await firstValueFrom(this.selectedReason$);
        if (reason != null && reason.isRequiredComment && !ctrl.get('comment')?.value) {
          return { commentRequired: true };
        }

        return null;
      },
    }
  );
  protected data?: RequestRevisionModalData | null;
  protected reasons: RevisionReason[] = [];
  protected readonly selectedReason$ = this.form.controls.reasonId.valueChanges.pipe(
    startWith<number | null>(null),
    map(id => (id == null ? null : this.reasons.find(r => r.id === id))),
    shareReplay(1)
  );

  ngOnInit() {
    this.data = this._config.data;
    this.reasons = Array.isArray(this.data?.reasons) ? this.data.reasons : [];
  }

  protected async onRequest() {
    const { comment } = this.form.getRawValue();
    const reason = await firstValueFrom(this.selectedReason$);
    if (reason != null) {
      if (await this.data?.requestRevisionFn?.(reason, comment ?? '')) {
        this._ref.close(true);
      }
    }
  }

  protected onCancel() {
    this._ref.close();
  }
}
